if (!customElements.get('norella-customer-stories')) {
  let customerStoryInstance = 0;

  customElements.define('norella-customer-stories', class extends HTMLElement {
    connectedCallback() {
      if (this.listeners) return;
      this.listeners = new AbortController();
      const signal = this.listeners.signal;
      const key = `nr-customer-instance-${++customerStoryInstance}`;
      const heading = this.querySelector('.nr-customer-heading');
      if (heading) {
        heading.id = `${key}-heading`;
        this.setAttribute('aria-labelledby', heading.id);
      }
      if (this.dataset.mode !== 'carousel') return;

      this.track = this.querySelector('.nr-customer-track');
      this.cards = Array.from(this.querySelectorAll('.nr-customer-card'));
      this.controls = this.querySelector('.nr-customer-controls');
      this.status = this.querySelector('.nr-customer-status');
      this.progress = this.querySelector('.nr-customer-progress-fill');
      this.arrows = Array.from(this.querySelectorAll('[data-customer-step]'));
      if (!this.track || !this.cards.length) return;
      this.track.id = `${key}-track`;
      this.arrows.forEach((button) => button.setAttribute('aria-controls', this.track.id));
      this.stops = [];
      this.current = 0;
      this.requested = null;
      this.interacted = false;

      this.addEventListener('click', (event) => {
        const button = event.target.closest?.('[data-customer-step]');
        if (!button || !this.arrows.includes(button) || button.disabled) return;
        const direction = Number(button.dataset.customerStep);
        if (direction !== -1 && direction !== 1) return;
        this.show((this.requested ?? this.current) + direction);
      }, { signal });

      this.track.addEventListener('keydown', (event) => {
        if (event.target !== this.track || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        const current = this.requested ?? this.current;
        const destinations = { ArrowLeft:current - 1, ArrowRight:current + 1, Home:0, End:this.stops.length - 1 };
        if (!Object.prototype.hasOwnProperty.call(destinations, event.key)) return;
        event.preventDefault();
        this.show(destinations[event.key]);
      }, { signal });

      const releaseTarget = () => {
        this.requested = null;
        this.interacted = true;
      };
      this.track.addEventListener('pointerdown', releaseTarget, { passive:true, signal });
      this.track.addEventListener('wheel', releaseTarget, { passive:true, signal });
      this.track.addEventListener('scroll', () => {
        if (!this.scrollFrame) {
          this.scrollFrame = requestAnimationFrame(() => {
            this.scrollFrame = null;
            this.updateState();
          });
        }
        this.settle();
      }, { passive:true, signal });

      const queueMeasure = () => {
        if (this.resizeFrame) return;
        this.resizeFrame = requestAnimationFrame(() => {
          this.resizeFrame = null;
          this.measure();
        });
      };
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(queueMeasure);
        this.resizeObserver.observe(this.track);
        this.cards.forEach((card) => this.resizeObserver.observe(card));
      } else {
        window.addEventListener('resize', queueMeasure, { passive:true, signal });
      }
      document.fonts?.ready.then(() => { if (this.isConnected) queueMeasure(); });
      this.measure();
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.listeners = null;
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.resizeFrame);
      clearTimeout(this.settleTimer);
      this.scrollFrame = null;
      this.resizeFrame = null;
      this.requested = null;
    }

    measure() {
      if (!this.track || !this.isConnected) return;
      const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const origin = this.track.getBoundingClientRect().left;
      const position = this.track.scrollLeft;
      const stops = [0];
      this.cards.forEach((card) => {
        const offset = Math.min(maximum, Math.max(0, card.getBoundingClientRect().left - origin + position));
        if (offset - stops[stops.length - 1] > 1) stops.push(offset);
      });
      if (maximum - stops[stops.length - 1] > 1) stops.push(maximum);
      const changed = stops.length !== this.stops.length || stops.some((stop, index) => Math.abs(stop - this.stops[index]) > 1);
      if (changed) {
        const hadStops = this.stops.length > 0;
        this.stops = stops;
        this.requested = null;
        if (hadStops) this.track.scrollTo({ left:stops[Math.min(this.current, stops.length - 1)], behavior:'instant' });
      }
      this.updateState();
    }

    updateState() {
      const maximum = this.stops[this.stops.length - 1] || 0;
      const position = Math.max(0, Math.min(maximum, this.track.scrollLeft));
      this.current = this.stops.reduce((nearest, stop, index) =>
        Math.abs(stop - position) < Math.abs(this.stops[nearest] - position) ? index : nearest, 0);
      if (this.controls) this.controls.hidden = maximum <= 1;
      this.arrows.forEach((button) => {
        button.disabled = Number(button.dataset.customerStep) < 0 ? position <= 1 : position >= maximum - 1;
      });
      const visibleFraction = Math.min(1, this.track.clientWidth / Math.max(1, this.track.scrollWidth));
      const progress = maximum > 1 ? visibleFraction + (1 - visibleFraction) * position / maximum : 1;
      this.progress?.style.setProperty('--nr-customer-progress', `${progress * 100}%`);
    }

    show(index) {
      if (!Number.isFinite(index) || !this.stops.length) return;
      this.interacted = true;
      this.requested = Math.max(0, Math.min(Math.trunc(index), this.stops.length - 1));
      this.track.scrollTo({
        left:this.stops[this.requested],
        behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
      });
      this.settle();
    }

    settle() {
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => {
        this.requested = null;
        this.updateState();
        if (!this.status || !this.interacted) return;
        const viewport = this.track.getBoundingClientRect();
        const visible = this.cards.flatMap((card, index) => {
          const rect = card.getBoundingClientRect();
          const overlap = Math.min(rect.right, viewport.right) - Math.max(rect.left, viewport.left);
          return overlap >= rect.width * .5 ? [index + 1] : [];
        });
        if (visible.length) {
          const first = visible[0];
          const last = visible[visible.length - 1];
          this.status.textContent = first === last ? `Review ${first} of ${this.cards.length}` : `Reviews ${first} to ${last} of ${this.cards.length}`;
        }
      }, 180);
    }
  });
}
